import { useEffect } from 'react';
import { useConnectionContext } from './ConnectionContext';
import type { UtrvConnection } from '@features/assistant/connections/types';
import { ValueDataSourceConnection, ValueDataSourceType } from '@g17eco/types/universalTrackerValue';
import { isUtrvDocumentConnection } from './utils';

const createValueDataSourceItem = (connection: UtrvConnection): ValueDataSourceConnection => {
  if (isUtrvDocumentConnection(connection)) {
    return {
      type: ValueDataSourceType.DocumentConnection,
      data: {
        documentId: connection.documentId,
        relevantUtrId: connection._id,
        value: connection.value,
        valueListCode: connection.valueListCode,
        unit: connection.unit,
        numberScale: connection.numberScale,
      },
    };
  }

  const integrationCode = Object.values(connection.variables).find(
    (variable) => variable.integrationCode,
  )?.integrationCode;

  const { value, unit, numberScale, _id: connectionId, calculationGroupCode, valueListCode } = connection;

  if (integrationCode) {
    return {
      type: ValueDataSourceType.ExternalConnection,
      data: { value, unit, numberScale, calculationGroupCode, connectionId, valueListCode, integrationCode },
    };
  }

  return {
    type: ValueDataSourceType.SecondaryConnection,
    data: { value, unit, numberScale, calculationGroupCode, connectionId, valueListCode },
  };
};

export const usePopulateInputFromConnection = ({
  valueListCode,
  inputChangeHandler,
}: {
  valueListCode?: string;
  inputChangeHandler: ({ value, source }: { value: number | string; source: ValueDataSourceConnection }) => void;
}) => {
  const { inputData, setInputData, connection } = useConnectionContext();

  // Trigger input change in input components (e.g. NumberInput, TextInput) when inputData is set by clicking populate button in Connection.
  useEffect(() => {
    if (
      inputData !== undefined &&
      connection &&
      (!connection.valueListCode || connection.valueListCode === valueListCode)
    ) {
      inputChangeHandler({ value: inputData, source: createValueDataSourceItem(connection) });
      setInputData(undefined);
    }
  }, [inputChangeHandler, inputData, setInputData, connection, valueListCode]);
};
